import { useCallback, useRef, useState } from 'react'

import axios from 'axios'

import { cepClient } from 'adapters/http'

export type CepValidationStatus = 'idle' | 'valid' | 'invalid'

const NOT_FOUND_MESSAGE = 'CEP não encontrado'
const GENERIC_ERROR_MESSAGE = 'Não foi possível validar o CEP. Tente novamente.'

type UseValidateCepResult = {
  validateCep: (zipcode: string) => Promise<void>
  resetCepStatus: () => void
  isCepLoading: boolean
  cepStatus: CepValidationStatus
  cepErrorMessage: string | null
}

export const useValidateCep = (): UseValidateCepResult => {
  const [isCepLoading, setIsCepLoading] = useState(false)
  const [cepStatus, setCepStatus] = useState<CepValidationStatus>('idle')
  const [cepErrorMessage, setCepErrorMessage] = useState<string | null>(null)
  const requestIdRef = useRef(0)

  const resetCepStatus = useCallback(() => {
    requestIdRef.current += 1
    setIsCepLoading(false)
    setCepStatus('idle')
    setCepErrorMessage(null)
  }, [])

  const validateCep = useCallback(async (zipcode: string) => {
    const requestId = ++requestIdRef.current
    setIsCepLoading(true)

    try {
      await cepClient.validateZipcode(zipcode)
      if (requestIdRef.current !== requestId) return

      setCepStatus('valid')
      setCepErrorMessage(null)
    } catch (error) {
      if (requestIdRef.current !== requestId) return

      const isNotFound = axios.isAxiosError(error) && error.response?.status === 404
      setCepStatus('invalid')
      setCepErrorMessage(isNotFound ? NOT_FOUND_MESSAGE : GENERIC_ERROR_MESSAGE)
    } finally {
      if (requestIdRef.current === requestId) setIsCepLoading(false)
    }
  }, [])

  return { validateCep, resetCepStatus, isCepLoading, cepStatus, cepErrorMessage }
}
